
import { CheckCircle, Brain, Target, Users } from 'lucide-react';

const WhyShowUpWorksSection = () => {
  const principles = [
    {
      icon: Brain,
      title: "The Science Behind Not Overthinking",
      description: "Research shows that decision fatigue kills habit formation. By simplifying the choice to just 'show up again,' we eliminate the mental burden that causes most people to quit.",
      stats: "Studies prove: Simple systems have 73% higher success rates"
    },
    {
      icon: Target,
      title: "PACT Framework Effectiveness", 
      description: "Unlike traditional goal-setting, PACT focuses on process over outcomes. This evidence-based approach helps you build the neural pathways that make habits automatic.",
      stats: "PACT users report 2.5x better habit consistency"
    },
    {
      icon: Users,
      title: "Community-Driven Accountability",
      description: "Showing up is easier when you're not alone. Our community of like-minded individuals provides gentle accountability without judgment or pressure.",
      stats: "67+ people already committed to showing up"
    },
    {
      icon: CheckCircle,
      title: "Minimalist by Design",
      description: "Less is more when it comes to habits. Our intentionally simple interface removes distractions and focuses on what matters: showing up consistently.",
      stats: "90% of users prefer simple over complex habit trackers"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-white relative overflow-hidden">
      {/* Geometric pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-20">
        <div className="absolute top-0 left-0 w-full h-full" 
             style={{
               backgroundImage: `
                 radial-gradient(circle at 25% 25%, #4c6ef5 2px, transparent 2px),
                 radial-gradient(circle at 75% 75%, #ff8a65 2px, transparent 2px)
               `,
               backgroundSize: '60px 60px'
             }}>
        </div>
      </div>

      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-6 h-6 bg-orange-500 rounded-full animate-pulse"></div>
        <div className="absolute bottom-32 left-16 w-4 h-4 bg-blue-600 rounded-full animate-pulse delay-500"></div>
        <div className="absolute top-1/2 right-1/4 w-3 h-3 bg-purple-500 rounded-full animate-pulse delay-1000"></div>
      </div>

      <div className="container max-w-6xl mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-6">
            <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-2 tracking-tight">
              Why "Show Up Again" Works
            </h2>
            <div className="h-1 bg-gradient-to-r from-blue-600 via-purple-500 to-orange-500 rounded-full mx-auto w-48"></div>
          </div>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-medium">
            The science and psychology behind our "don't overthink it" approach to lasting habit formation
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {principles.map((principle, index) => {
            const IconComponent = principle.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={index}
                className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-slate-200 hover:border-blue-600/30 ${
                  isEven ? 'transform hover:-translate-y-2' : 'transform hover:translate-y-2'
                }`}
              >
                {/* Number badge */}
                <div className="absolute -top-4 -left-4 w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                  {index + 1}
                </div>

                <div className="flex items-start gap-6 mb-6">
                  <div className="p-4 bg-gradient-to-br from-blue-600/10 to-purple-500/10 rounded-xl group-hover:from-blue-600/20 group-hover:to-purple-500/20 transition-all duration-300">
                    <IconComponent className="w-8 h-8 text-blue-600 group-hover:text-purple-500 transition-colors duration-300" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-black text-slate-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                      {principle.title}
                    </h3>
                    <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-4 py-2 rounded-xl font-bold text-sm shadow-md inline-block transform group-hover:scale-105 transition-transform duration-300">
                      ✨ {principle.stats}
                    </div>
                  </div>
                </div>
                
                <p className="text-slate-600 leading-relaxed text-lg font-medium">
                  {principle.description}
                </p>

                {/* Decorative corner */}
                <div className="absolute bottom-4 right-4 w-3 h-3 bg-gradient-to-br from-blue-600 to-purple-500 rounded-full opacity-30 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-blue-600 to-purple-500 rounded-3xl p-8 max-w-4xl mx-auto transform hover:scale-105 transition-all duration-300 shadow-2xl text-white">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8" />
              <h3 className="text-3xl font-black">
                The Bottom Line
              </h3>
            </div>
            <p className="text-xl font-medium leading-relaxed opacity-95">
              Showing up consistently is a learnable skill. Once you master this foundational behavior, 
              every other habit and goal becomes achievable. Don't overthink it - just show up again.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyShowUpWorksSection;
